import { useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AudioRecorder } from "@/components/smartfile/AudioRecorder";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface PatientInfo {
  name: string;
  birth_date: string | null;
  avatar_url?: string | null;
}

interface AudioOverlayProps {
  onComplete: (file: File) => void;
  onClose: () => void;
  patient?: PatientInfo;
}

const calculateAge = (birthDate: string | null): number | null => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

export function AudioOverlay({ onComplete, onClose, patient }: AudioOverlayProps) {
  useEffect(() => {
    // Prevenir scroll do body
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const initials = patient?.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  const age = patient ? calculateAge(patient.birth_date) : null;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm">
      <div className="relative h-full w-full overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background/80 backdrop-blur px-4 py-3">
          <h2 className="text-lg font-semibold">Gravar Áudio</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {patient && (
          <div className="border-b bg-muted/30 px-4 py-3">
            <div className="container mx-auto max-w-2xl">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  {patient.avatar_url && <AvatarImage src={patient.avatar_url} alt={patient.name} />}
                  <AvatarFallback className="bg-primary/10 text-primary text-sm">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="font-semibold text-foreground">{patient.name}</span>
                  {age !== null && (
                    <span className="text-sm text-muted-foreground">{age} anos</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="container mx-auto max-w-2xl p-4">
          <AudioRecorder
            onRecordingComplete={(file) => {
              onComplete(file);
              onClose();
            }}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  );
}
